import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import {
  AssessmentRule,
  CreateAssessmentRule,
  UpdateAssessmentRule,
} from '../../../models/assessment-rule.model';
import { AssessmentRuleService } from '../../../services/assessment-rule.service';
import { AssessmentService } from '../../../services/assessment.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';

@Component({
  selector: 'app-assessment-rule-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    ToolbarModule,
    ToastModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
  ],
  templateUrl: './assessment-rule-create-or-update.component.html',
})
export class AssessmentRuleCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private formGroupService = inject(FormGroupService);
  private assessmentRuleService = inject(AssessmentRuleService);
  private assessmentService = inject(AssessmentService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    condition: [null, [Validators.required, Validators.maxLength(255)]],
    failureMessage: [null, [Validators.required, Validators.maxLength(255)]],
    assessmentId: [null, [Validators.required]],
    isActive: [true],
  });

  loading = signal(false);
  assessmentRuleId = signal<string | null>(null);

  assessmentsDropdown = signal<DropdownOption[]>([]);
  assessmentSelectLoading = signal(false);

  ngOnInit(): void {
    this.loadAssessmentsDropdown();

    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.assessmentRuleId.set(id);
      this.loadData(id);
    }
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.assessmentRuleId();
    const body = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    body.assessmentId = body.assessmentId?.value;

    if (id) {
      request$ = this.assessmentRuleService.update(
        id,
        body as UpdateAssessmentRule
      );
    } else {
      delete body.id;
      request$ = this.assessmentRuleService.create(
        body as CreateAssessmentRule
      );
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Regra de avaliação salva com sucesso',
          life: 2000,
        });

        this.router.navigate(['/assessment-rules']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length === 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a regra de avaliação',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private loadAssessmentsDropdown(): void {
    this.assessmentSelectLoading.set(true);

    this.assessmentService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.assessmentSelectLoading.set(false);
        const options = response.data as DropdownOption[];
        this.assessmentsDropdown.set(options);
      },
      error: () => {
        this.assessmentSelectLoading.set(false);

        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar as avaliações',
          life: 3000,
        });
      },
    });
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.assessmentRuleService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const data = response.data as AssessmentRule;

        this.formGroup.patchValue({
          id: data.id,
          name: data.name,
          condition: data.condition,
          failureMessage: data.failureMessage,
          assessmentId: {
            value: data.assessmentId,
            label: data.assessmentName,
          },
          isActive: data.isActive,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a regra de avaliação',
            life: 3000,
          });
        }
      },
    });
  }
}
